import { Text, TextVariants } from '@/components';
import APP_CONFIG from '@/config/app';
import Image from 'next/image';
import React, { FC, ReactElement, ReactNode } from 'react';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const {
  MAIN_GAP,
  WHOLESTEP_DECREMENT,
  TEXT: {
    BIG_HEADING,
    BIG_HEADING_COLOR,
    BIG_SUBHEADING,
    BIG_SUBHEADING_COLOR,
    BODY,
    BODY_COLOR,
    BODY_SUBCONTENT,
    BODY_SUBCONTENT_COLOR,
  },
} = APP_CONFIG.STYLES;
const spacingConfig = {
  gap: `gap${MAIN_GAP}${WHOLESTEP_DECREMENT}`,
  gapBetweenLeftColorElementAndLeftIcon: `gap${MAIN_GAP}${WHOLESTEP_DECREMENT}`,
  gapBetweenMainTextAndGroupOfOtherSubTextBlocks: 'gap-0',
  gapBetweenSubTextBlocks: 'gap-0',
};
const sizingConfig = {
  leftIconWidth: 36,
  leftIconHeight: 36,
  leftIconWidthWhenInHeader: 44,
  leftIconHeightWhenInHeader: 44,
  rightIconWidth: 16,
  rightIconHeight: 16,
};
const roundednessConfig = {
  leftColorRoundedness: 'rounded-full',
};
const textConfig = {
  mainTextSize: BODY,
  mainTextWhenInHeaderSize: BIG_HEADING,
  mainTextColor: BODY_COLOR,
  mainTextWhenInHeaderColor: BIG_HEADING_COLOR,
  subTextSize: BODY_SUBCONTENT,
  subTextSizeWhenInHeader: BIG_SUBHEADING,
  subTextColor: BODY_SUBCONTENT_COLOR,
  subTextColorWhenInHeader: BIG_SUBHEADING_COLOR,
  subSubTextSize: BODY_SUBCONTENT,
  subSubTextColor: BODY_SUBCONTENT_COLOR,
  subSubSubTextSize: BODY_SUBCONTENT,
  subSubSubTextColor: BODY_SUBCONTENT_COLOR,
  subSubSubSubTextSize: BODY_SUBCONTENT,
  subSubSubSubTextColor: BODY_SUBCONTENT_COLOR,
};
export interface ListItemProps {
  mainText: string | ReactNode;
  subText?: string | ReactNode;
  subSubText?: string | ReactNode;
  subSubSubText?: string | ReactNode;
  subSubSubSubText?: string | ReactNode;
  mainTextSize?: TextVariants['size'];
  mainTextColor?: TextVariants['color'];
  subTextSize?: TextVariants['size'];
  subTextColor?: TextVariants['color'];
  subSubTextSize?: TextVariants['size'];
  subSubTextColor?: TextVariants['color'];
  subSubSubTextSize?: TextVariants['size'];
  subSubSubTextColor?: TextVariants['color'];
  subSubSubSubTextSize?: TextVariants['size'];
  subSubSubSubTextColor?: TextVariants['color'];
  isHeader?: boolean;
  leftIconUrl?: string;
  leftIcon?: ReactNode;
  leftIconWidth?: number;
  leftIconHeight?: number;
  leftIconBorderRadius?: string;
  rightIconUrl?: string;
  rightIcon?: ReactNode;
  align?: 'left' | 'center' | 'right';
  className?: string;
  fullWidth?: boolean;
  leftColor?: string;
  shouldMiddleAlignIconAndText?: boolean;
  chipText?: string;
  setChipToNoMargin?: boolean;
}
const ListItem: FC<ListItemProps> = ({
  isHeader = false,
  mainText,
  subText,
  subSubText,
  subSubSubText,
  subSubSubSubText,
  mainTextSize = isHeader
    ? textConfig.mainTextWhenInHeaderSize
    : textConfig.mainTextSize,
  mainTextColor = isHeader
    ? textConfig.mainTextWhenInHeaderColor
    : textConfig.mainTextColor,
  subTextSize = isHeader
    ? textConfig.subTextSizeWhenInHeader
    : textConfig.subTextSize,
  subTextColor = isHeader
    ? textConfig.subTextColorWhenInHeader
    : textConfig.subTextColor,
  subSubTextSize = isHeader
    ? textConfig.subTextSizeWhenInHeader
    : textConfig.subSubTextSize,
  subSubTextColor = isHeader
    ? textConfig.subTextColorWhenInHeader
    : textConfig.subSubTextColor,
  subSubSubTextSize = isHeader
    ? textConfig.subTextSizeWhenInHeader
    : textConfig.subSubSubTextSize,
  subSubSubTextColor = isHeader
    ? textConfig.subTextColorWhenInHeader
    : textConfig.subSubSubTextColor,
  subSubSubSubTextSize = isHeader
    ? textConfig.subTextSizeWhenInHeader
    : textConfig.subSubSubSubTextSize,
  subSubSubSubTextColor = isHeader
    ? textConfig.subTextColorWhenInHeader
    : textConfig.subSubSubSubTextColor,
  leftIconUrl,
  leftIcon,
  leftIconWidth = isHeader
    ? sizingConfig.leftIconWidthWhenInHeader
    : sizingConfig.leftIconWidth,
  leftIconHeight = isHeader
    ? sizingConfig.leftIconHeightWhenInHeader
    : sizingConfig.leftIconHeight,
  leftIconBorderRadius = ``,
  rightIconUrl,
  rightIcon,
  align = 'left',
  className = '',
  fullWidth = true,
  leftColor,
  shouldMiddleAlignIconAndText,
  chipText,
  setChipToNoMargin = false,
}) => {
  const textLines = [
    mainText,
    subText,
    subSubText,
    subSubSubText,
    subSubSubSubText,
  ].filter((text) => text !== undefined && text !== null).length;
  const chipConfig = {
    baseStyles: `${
      setChipToNoMargin ? 'mx-0' : 'mx-xs'
    } px-xs my-0 py-0.5 rounded-2xs text-xs bg-primary/10 text-primary flex items-center justify-center self-center`,
    whenInHeader: 'text-sm',
  };
  const shouldAlignMiddle =
    shouldMiddleAlignIconAndText !== undefined
      ? shouldMiddleAlignIconAndText
      : isHeader || textLines <= 2;
  const horizontalAlignmentClass =
    align === 'right'
      ? 'justify-end flex-row-reverse'
      : align === 'center'
      ? 'justify-center'
      : 'justify-start';
  const verticalAlignmentClass = shouldAlignMiddle
    ? 'items-center'
    : 'items-start';
  const renderLeftColor = () => {
    if (leftColor) {
      return (
        <div
          className={`${roundednessConfig.leftColorRoundedness} ${leftColor}`}
          style={{
            width: `${leftIconWidth}px`,
            height: `${leftIconHeight}px`,
            flexShrink: 0,
            borderRadius: '50%',
            overflow: 'hidden',
          }}
        ></div>
      );
    }
    return null;
  };
  const renderLeftIcon = () => {
    return leftIcon ? (
      React.cloneElement(leftIcon as ReactElement, {
        style: {
          width: `${leftIconWidth}px`,
          height: `${leftIconHeight}px`,
          flexShrink: 0,
          borderRadius: `${leftIconBorderRadius}`,
          overflow: 'hidden',
        },
      })
    ) : leftIconUrl ? (
      <div
        style={{
          width: `${leftIconWidth}px`,
          height: `${leftIconHeight}px`,
          borderRadius: `${leftIconBorderRadius}`,
          overflow: 'hidden',
        }}
      >
        <Image
          src={leftIconUrl}
          alt="left icon"
          width={leftIconWidth}
          height={leftIconHeight}
          style={{ objectFit: 'cover' }}
        />
      </div>
    ) : null;
  };
  const renderRightIcon = () => {
    if (rightIcon) {
      return React.cloneElement(rightIcon as ReactElement, {
        style: {
          width: `${sizingConfig.rightIconWidth}px`,
          height: `${sizingConfig.rightIconHeight}px`,
          flexShrink: 0,
        },
      });
    }
    if (rightIconUrl) {
      return (
        <Image
          src={rightIconUrl}
          alt="right icon"
          width={sizingConfig.rightIconWidth}
          height={sizingConfig.rightIconHeight}
        />
      );
    }
    return null;
  };
  const leftIconElement = renderLeftIcon();
  const leftColorElement = renderLeftColor();
  const rightIconElement = renderRightIcon();
  return (
    <div
      className={`flex flex-row
          ${verticalAlignmentClass}
          ${horizontalAlignmentClass}
          ${spacingConfig.gap} 
          ${fullWidth ? 'w-full' : ''}
          ${className} 
          ${borderClassToVisualizeElementsAndSpacing}`}
    >
      {(leftIconElement || leftColorElement) && (
        <div
          className={`flex flex-row 
              ${spacingConfig.gapBetweenLeftColorElementAndLeftIcon}
              ${borderClassToVisualizeElementsAndSpacing}`}
        >
          {leftColorElement}
          {leftIconElement}
        </div>
      )}
      {}
      <div
        className={`flex flex-col ${
          align === 'right'
            ? 'items-end'
            : align === 'center'
            ? 'items-center'
            : 'items-start'
        } flex-1
          ${spacingConfig.gapBetweenMainTextAndGroupOfOtherSubTextBlocks}
          ${borderClassToVisualizeElementsAndSpacing}`}
      >
        {}
        <div
          className={`flex flex-row 
                ${borderClassToVisualizeElementsAndSpacing}`}
        >
          {typeof mainText === 'string' ? (
            <Text
              value={mainText}
              size={mainTextSize}
              color={mainTextColor}
              className={`${borderClassToVisualizeElementsAndSpacing} ${isHeader}`}
            />
          ) : (
            mainText
          )}
          {chipText && (
            <div
              className={`${chipConfig.baseStyles} ${
                isHeader ? chipConfig.whenInHeader : ''
              }`}
            >
              {chipText}
            </div>
          )}
        </div>
        {}
        <div
          className={`flex flex-col 
              ${spacingConfig.gapBetweenSubTextBlocks}
              ${borderClassToVisualizeElementsAndSpacing}`}
        >
          {subText && (
            <div
              className={`flex flex-col 
                ${borderClassToVisualizeElementsAndSpacing}`}
            >
              {typeof subText === 'string' ? (
                <Text
                  value={subText}
                  size={subTextSize}
                  color={subTextColor}
                  className={borderClassToVisualizeElementsAndSpacing}
                />
              ) : (
                subText
              )}
            </div>
          )}
          {subSubText && (
            <div
              className={`flex flex-col 
                ${borderClassToVisualizeElementsAndSpacing}`}
            >
              {typeof subSubText === 'string' ? (
                <Text
                  value={subSubText}
                  size={subSubTextSize}
                  color={subSubTextColor}
                  className={borderClassToVisualizeElementsAndSpacing}
                />
              ) : (
                subSubText
              )}
            </div>
          )}
          {subSubSubText && (
            <div
              className={`flex flex-col 
                ${borderClassToVisualizeElementsAndSpacing}`}
            >
              {typeof subSubSubText === 'string' ? (
                <Text
                  value={subSubSubText}
                  size={subSubSubTextSize}
                  color={subSubSubTextColor}
                  className={borderClassToVisualizeElementsAndSpacing}
                />
              ) : (
                subSubSubText
              )}
            </div>
          )}
          {subSubSubSubText && (
            <div
              className={`flex flex-col 
                ${borderClassToVisualizeElementsAndSpacing}`}
            >
              {typeof subSubSubSubText === 'string' ? (
                <Text
                  value={subSubSubSubText}
                  size={subSubSubSubTextSize}
                  color={subSubSubSubTextColor}
                  className={borderClassToVisualizeElementsAndSpacing}
                />
              ) : (
                subSubSubSubText
              )}
            </div>
          )}
        </div>
      </div>
      {rightIconElement && (
        <div
          className={`flex ${shouldAlignMiddle ? 'items-center' : 'items-start'}
            ${borderClassToVisualizeElementsAndSpacing}`}
        >
          {rightIconElement}
        </div>
      )}
    </div>
  );
};
export default ListItem;
